/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Inject, Input, OnChanges, OnDestroy, signal} from '@angular/core';
import {handleCopyModalResult} from '../common/util/handle-copy-modal-result';
import {ToastrService} from 'ngx-toastr';

import {AngularModalService} from '../common/modal/angular-modal.service';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {LineItemCopyModalComponent} from './line-item-copy-modal.component';
import template from './line-item-context-actions.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {StarService} from '../../components/star/star.service';
import {StateService} from '@uirouter/core';
import {LineItemModel} from './line-item-api.service';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-line-item-context-actions',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LineItemContextActionsComponent implements OnChanges, OnDestroy {
  @Input() state: SignalAdminResourcePageState<LineItemModel>;
  @Input() viewType: number;

  private readonly isStarredState = signal(false);
  private readonly starPendingState = signal(false);
  get isStarred(): boolean { return this.isStarredState(); }
  set isStarred(value: boolean) { this.isStarredState.set(value); }
  get starPending(): boolean { return this.starPendingState(); }
  set starPending(value: boolean) { this.starPendingState.set(value); }
  private destroyed = false;

  constructor(
    private readonly $state: StateService,
    private readonly toastr: ToastrService,
    @Inject(StarService) private readonly StarService: any,
    private readonly angularModalService: AngularModalService
  ) {
  }

  get objectId(): string {
    return this.state?.model()?.id;
  }

  get objectType(): string {
    return OBJECT_TYPE.LineItem.type;
  }

  ngOnChanges(): void {
    this.refreshStar();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  copy(): void {
    const model = this.state?.model();
    if (this.state?.readOnly() || !model) {
      return;
    }

    const modalRef = this.angularModalService.show(LineItemCopyModalComponent, {
      initialState: {model},
      class: 'modal-lg'
    });
    (modalRef.content as LineItemCopyModalComponent).onCopied.subscribe((result) => {
      handleCopyModalResult(() => {
        if (result.destination) {
          this.$state.go('app.adv.line-items.line-item', {id: result.lineItemId}, {inherit: false});
        } else {
          this.toastr.success('A copy of this line item was successfully created.', 'Line Item Copied');
        }
      });
    });
  }

  toggleStar(): void {
    if (!this.objectId || this.starPending) {
      return;
    }
    this.starPending = true;
    const newStarred = !this.isStarred;
    this.StarService.updateStar(this.objectId, OBJECT_TYPE.LineItem, newStarred).then(() => {
      if (this.destroyed) {
        return;
      }
      this.isStarred = newStarred;
      this.starPending = false;
    }, () => {
      if (this.destroyed) {
        return;
      }
      this.refreshStar();
      this.starPending = false;
      this.toastr.error('Failed to update the line item star', 'Star update failed');
    });
  }

  private refreshStar(): void {
    if (!this.objectId) {
      this.isStarred = false;
      return;
    }
    this.isStarred = this.StarService.isStarred(this.objectId, OBJECT_TYPE.LineItem);
  }
}
